import * as R from 'ramda'
import {
  ClientFrontlineConfig,
  contractDate,
  EquipmentCategory,
  Offer,
  OpportunityContract,
  SyncStatus
} from 'app/types/types'
import moment from 'moment'
import { Constants, MAIN_TAB_LIST } from './client-constants'
import { getOffersPromise } from '../db/db'
import i18next, { t } from 'i18next'
import { BreadcrumbItem } from 'app/types/types'
import Bowser from 'bowser'
import { NavigationTab, NavigationTabList } from 'app/types/ui/grouping'
import { OpportunityStatus, StatusBadgeProps } from 'app/types/ui/opportunity'
import { isFinalizationIncomplete } from 'app/app/common/offer-state-predicates'
import { isPrepareInprogress } from './prepare-utils'
import { isGroupingInprogress } from './grouping-utils'
import { isOfferingInprogress } from './offer'

export const formatPrice = (price: number, lang: string, currency: string | undefined): string => {
  const locale = lang && lang.trim() ? lang.replace('_', '-') : 'en-GB'
  return Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'EUR',
    minimumFractionDigits: 2
  }).format(price)
}

export const currencySymbol = (lang: string, currency: string): string =>
  formatPrice(0, lang, currency).replace(/[\d,.]/g, '')

// export const discountPricingValidation = (priceBreakdown: GroupedPriceBreakdown) => {
//   const calculateAverage = (values: number[]) => values.reduce((acc, value) => acc + value, 0) / values.length

//   const maximumDiscoutPercentageAllowed = calculateAverage(
//     priceBreakdown.groups.map(group => group.maximumSalesPricePercentageWithoutApproval)
//   )

//   const totalAnnualListPrice =
//     priceBreakdown.coreOfferingAnnualListPrice + priceBreakdown.valueAddedServicesAnnualListPrice
//   const totalPriceWithExtraService = priceBreakdown.annualListPrice

//   const validatePercentage = 100 - ((annualPriceWithExtraService - annualListPrice) / annualPriceWithExtraService) * 100
//   return { maximumDiscoutPercentageAllowed, validatePercentage }
// }

export const equipmentCategories: EquipmentCategory[] = ['elevator', 'escalator']

// Offer -> Boolean
export const isOfferUnsaved = (offer: Offer) =>
  // new offer, no server timestamp
  !offer.modified_server ||
  // has already been saved and has been modified locally?
  moment(offer.modified_client).isAfter(moment(offer.modified_server))

export const isValidUrl = (url: string) => {
  const match = url.match(Constants.VALID_URL_REGEX)
  return match ? true : false
}

export const getFrontlineEnabledTechnicalPlatforms = (frontlineConfig: ClientFrontlineConfig): string[] =>
  Object.keys(frontlineConfig.technicalPlatforms)

export const getFrontlineEnabledEscalatorTypes = (frontlineConfig: ClientFrontlineConfig): string[] =>
  Object.keys(frontlineConfig.escalatorTypes)

export const getEarliestDate = (contracts: OpportunityContract[], sortBy: contractDate) => {
  const sortStart = (a: OpportunityContract, b: OpportunityContract) => Date.parse(a.start) - Date.parse(b.start)
  const sortEnd = (a: OpportunityContract, b: OpportunityContract) =>
    a.end && b.end ? Date.parse(a.end) - Date.parse(b.end) : a.end ? -1 : 1
  if (contracts.length > 0) {
    const sortContract = [...contracts]
    sortContract.sort(sortBy === 'start' ? sortStart : sortEnd)
    return sortContract[0][sortBy]
  }
  return null
}

export const generateBreadcrumbs = (pathname: string, offer?: Offer | null): BreadcrumbItem[] => {
  const segments = pathname.split('/').filter(Boolean)
  const breadcrumbs: BreadcrumbItem[] = [{ label: t('navigation.refresh.home'), link: Constants.HEADER_HOME_LINK }]

  // Always add the opportunity/customer as second breadcrumb
  if (offer) {
    breadcrumbs.push({
      label: offer.customer.name || offer.opportunity?.name || '',
      link: `/${offer.opportunityId}/prepare`
    })
  }

  const mainPageLabels: Record<string, string> = {
    prepare: t('navigation.refresh.prepare'),
    grouping: t('navigation.refresh.grouping'),
    offers: t('navigation.refresh.offer'),
    contract: t('navigation.refresh.contract')
  }

  const subPageLabels: Record<string, Record<string, string>> = {
    prepare: {
      account: t('navigation.refresh.account'),
      contact: t('navigation.refresh.contact'),
      contract: t('navigation.refresh.contract'),
      location: t('navigation.refresh.location'),
      elevator: t('navigation.refresh.elevator'),
      escalator: t('navigation.refresh.escalator')
    },
    offers: {
      overview: t('navigation.refresh.overview'),
      edit: t('navigation.refresh.edit'),
      'view-equipments': t('navigation.refresh.viewEquipments'),
      'offer-overview': t('navigation.refresh.offerOverview')
    }
  }

  const opportunityId = segments[0]
  const mainPage = segments[1]
  const subPage = segments[2]

  if (mainPage && mainPageLabels[mainPage]) {
    const hasSubPage = subPage && subPageLabels[mainPage]?.[subPage]

    breadcrumbs.push({
      label: mainPageLabels[mainPage],
      link: hasSubPage && opportunityId ? `/${opportunityId}/${mainPage}` : undefined,
      isActive: !hasSubPage
    })

    if (hasSubPage) {
      breadcrumbs.push({
        label: subPageLabels[mainPage][subPage],
        link: `/${opportunityId}/${mainPage}/${subPage}`,
        isActive: true
      })
    }
  }

  return breadcrumbs
}

export const checkForSAPOIInprogress = () => {
  getOffersPromise()
    .then((dbOffers: Offer[]) => {
      const OITimesatamps = dbOffers
        .map(o => o.sapOIStartTimestamp)
        .filter(s => s != null && moment(s).isAfter(moment().subtract(25, 'minutes')))
      // SAPOIInProgress.set(OITimesatamps.length ? true : false)
      console.log('SAP OI in progress:', OITimesatamps)
    })
    .catch(err => {
      // handleError(new Error(`checkForSAPOIInprogress error: ${err}`))
      console.log('Error checking for SAP OI in progress:', err)
    })
}

export const getIcon = (type: string) => {
  switch (type) {
    case 'success':
      return 'icon-check-circle-filled'
    case 'error':
      return 'icon-alert-triangle-filled'
    case 'modal':
      return ''
    case 'info':
    case 'warning':
    default:
      return 'icon-warning-circle-filled'
  }
}

export const getLocationConfig = (locationKey: string, frontLineConfig: ClientFrontlineConfig) => {
  const fetchData = frontLineConfig?.buildingQuestions.find((q: any) => q.key === locationKey)
  return fetchData && fetchData.choices && (fetchData.choices as any).some((d: string) => typeof d === 'string')
    ? R.head(fetchData.choices as string[])
    : null
}
export const getEquipmentText = (equipment: EquipmentCategory | string, count: number): string =>
  `customers.refresh.${equipment}${count > 1 ? 's' : ''}`

export const capitalizeIfEnglish = (text: string): string => {
  const lang = i18next.language
  if (lang && lang.startsWith('en')) {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
  }
  return text
}
export const ensureSupportedBrowser = () => {
  if (typeof window === 'undefined' || !window.navigator) {
    return true
  }
  const bowser = Bowser.getParser(window.navigator.userAgent)
  console.log('Browser:', bowser.getBrowser())
  return (
    bowser.satisfies({
      chrome: '>67',
      firefox: '>66',
      safari: '>12',
      edge: '>79'
    }) || false
  )
}

export const multiValuedKeys = ['apfComponent']

export const getDataFromSessionStorage = <T>(key: string): T | null => {
  const data = sessionStorage.getItem(key)
  return data ? JSON.parse(data) : null
}
export const saveDataToSessionStorage = <T>(key: string, data: T): void => {
  sessionStorage.setItem(key, JSON.stringify(data))
}
export const removeDataFromSessionStorage = (key: string): void => {
  sessionStorage.removeItem(key)
}

export type PriceScoperType = { price: (currPrice: number) => number }

export const PriceScoper = (isMonthlyMode: boolean): PriceScoperType => ({
  price: (currPrice: number) => (isMonthlyMode ? currPrice / 12.0 : currPrice * 12.0)
})

export const isTabDisabled = (mainTabList: NavigationTabList[], tabValue: NavigationTab): boolean =>
  mainTabList.find((tab: NavigationTabList) => tab.value === tabValue)?.isDisabled || false

export const getBackPath = (
  opportunityId: string,
  mainPage: string,
  subPage: string | undefined,
  nestedSubPage: string | undefined,
  offer: Offer | null
): string | null => {
  switch (mainPage) {
    case 'prepare':
      return subPage ? `/${opportunityId}/prepare` : '/'

    case 'grouping':
      return `/${opportunityId}/prepare`

    case 'offers':
      if (subPage === 'edit') {
        return !offer?.tenderId || !offer?.tenderVersionId ? `/${opportunityId}/grouping` : `/${opportunityId}/offers`
      } else if (subPage === 'view-equipments') {
        return `/${opportunityId}/offers/edit`
      } else if (subPage === 'contract-renewal') {
        return `/${opportunityId}/prepare`
      }
      return subPage ? `/${opportunityId}/offers` : `/${opportunityId}/grouping`

    case 'contract':
      return `/${opportunityId}/offers`

    default:
      return null
  }
}
export const openFile =
  (filename: string, fileType: string = 'text/xml') =>
  (file: any) => {
    const link = document.createElement('a')
    const blob = new Blob([file], { type: fileType })
    const url = window.URL.createObjectURL(blob)

    link.href = url
    link.download = filename
    link.style.display = 'none'

    // Append to body for better browser compatibility
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return blob
  }

export const mergeOfferChanges = (currentOffer: Offer, updatedOffer: Partial<Offer>): Offer =>
  R.mergeWith(
    (currentOfferSubtree, updatedOfferSubtree) => {
      // Choose current offer subtree when the subtrees are equal, so we retain object identity for
      // faster React rendering.
      const equalTrees = R.equals(currentOfferSubtree, updatedOfferSubtree)
      return equalTrees ? currentOfferSubtree : updatedOfferSubtree
    },
    currentOffer,
    updatedOffer
  )

export const handleOfferPageNavigation = (offer: Offer | null | undefined, value: string): string => {
  if (!offer) {
    return '/'
  }

  const opportunityId = offer.opportunityId

  // If escalation flow is active AND offer is not finalized, navigate to contract-renewal page
  // Once completed (finalize attempted - in progress, errored, or finalized), show the versions
  // page like normal offers so finalization progress/errors are visible instead of re-opening the wizard
  if (value === MAIN_TAB_LIST.offers && offer.isEscalationFlow?.active && !offer.finalized && !offer.completed) {
    return `/${opportunityId}/${value}/contract-renewal`
  }

  if (value === MAIN_TAB_LIST.offers && !offer.completed && !offer.finalized) {
    return `/${opportunityId}/${value}/edit`
  }

  return `/${opportunityId}/${value}`
}

export const resolveStatus = (offer?: Offer, frontlineConfig?: ClientFrontlineConfig): OpportunityStatus => {
  if (offer) {
    const { finalized, discountApprovalStatus, sapContractNumber, tenderVersionNumber } = offer

    if (sapContractNumber != null) {
      return OpportunityStatus.SentToSap
    }

    if (finalized) {
      if (discountApprovalStatus === 'pending') {
        return OpportunityStatus.DiscountApprovalPending
      }
      if (discountApprovalStatus === 'rejected') {
        return OpportunityStatus.DiscountApprovalRejected
      }
    }
    if (isFinalizationIncomplete(offer)) {
      return OpportunityStatus.OfferStatusIncomplete
    }

    if (tenderVersionNumber != null) {
      return OpportunityStatus.OfferCreated
    }

    if (frontlineConfig && isPrepareInprogress(offer, frontlineConfig)) {
      return OpportunityStatus.PrepareInProgress
    }

    if (isGroupingInprogress(offer)) {
      return OpportunityStatus.GroupingInProgress
    }

    if (isOfferingInprogress(offer)) {
      return OpportunityStatus.OfferInProgress
    }
  }
  return OpportunityStatus.BeginPrepare
}

export const getOfferStatus = (
  offer?: Offer,
  frontlineConfig?: ClientFrontlineConfig,
  SFSyncStatus?: SyncStatus
): StatusBadgeProps => {
  const offerStatus = resolveStatus(offer, frontlineConfig)
  switch (offerStatus) {
    case OpportunityStatus.DiscountApprovalPending:
      return {
        status: offerStatus,
        variant: 'warning',
        label: `
          ${capitalizeIfEnglish(t('offerWizard.discountApproval.title'))}:
          ${capitalizeIfEnglish(t('offerWizard.discountApproval.pending'))}
        `
      }

    case OpportunityStatus.OfferStatusIncomplete:
      return {
        status: offerStatus,
        variant: 'warning',
        label: `
          ${capitalizeIfEnglish(t('offerWizard.status.incomplete'))}`
      }

    case OpportunityStatus.DiscountApprovalRejected:
      return {
        status: offerStatus,
        variant: 'error',
        label: `
        ${capitalizeIfEnglish(t('offerWizard.discountApproval.title'))}:
        ${capitalizeIfEnglish(t('offerWizard.discountApproval.rejected'))}
        `
      }
    case OpportunityStatus.SentToSap:
      return {
        status: offerStatus,
        variant: 'success',
        label: `
         ${capitalizeIfEnglish(t('customers.sentToSap'))}
        `
      }
    case OpportunityStatus.OfferCreated:
      return {
        status: offerStatus,
        variant: 'warning',
        label: `${capitalizeIfEnglish(t('customers.offerCreated'))} (v${offer?.tenderVersionNumber})`
      }
    case OpportunityStatus.OfferInProgress:
      return {
        status: offerStatus,
        variant: 'warning',
        label: `${capitalizeIfEnglish(t('customers.offerInProgress'))}`
      }

    case OpportunityStatus.GroupingInProgress:
      return {
        status: offerStatus,
        variant: 'warning',
        label: `${capitalizeIfEnglish(t('customers.refresh.newGroupingInProgress'))}`
      }

    case OpportunityStatus.PrepareInProgress:
      return {
        status: offerStatus,
        variant: 'warning',
        label: `${capitalizeIfEnglish(t('customers.refresh.newPrepareInProgress'))}`
      }

    case OpportunityStatus.BeginPrepare:
      if (SFSyncStatus && !SFSyncStatus.syncedWithSF) {
        return {
          status: offerStatus,
          variant: 'error',
          label: `${capitalizeIfEnglish(t('assessment.overview.initial.syncWithSalesForce'))}`
        }
      }
      return {
        status: offerStatus,
        variant: 'warning',
        label: `${capitalizeIfEnglish(t('customers.refresh.beginPrepareStatus'))}`
      }

    default:
      return {
        status: OpportunityStatus.BeginPrepare,
        variant: 'warning',
        label: `${capitalizeIfEnglish(t('customers.refresh.beginPrepareStatus'))}`
      }
  }
}
